const ENV = typeof process !== 'undefined' ? process.env || {} : {}

export const STORE_LOCATOR_CONFIGS = {
    isDevelopment: ENV.NODE_ENV === 'development',
    initialZoom: 6,
    centerZoom: 15,
    disableDefaultUI: false,
    gestureHandling: 'greedy',
    // Map theme selection
    mapTheme: 'custom', // Options: 'default', 'custom'
    // Restrict the viewport to a single world copy so the map cannot be panned
    // horizontally into repeated worlds, and cannot be scrolled vertically past
    // the poles into the grey out-of-bounds area.
    mapRestriction: {
        // Longitude is kept just under the full 360° span. Using the full
        // -180/180 range makes Google Maps treat it as "no horizontal limit"
        // and allows infinite horizontal wrapping, so we shrink it slightly to
        // force the map to show a single, finite world horizontally.
        latLngBounds: {north: 85, south: -85, west: -179.9999, east: 179.9999},
        strictBounds: true
    },
    STORE_LOCATOR_DISTANCE: parseInt(ENV.STORE_LOCATOR_DISTANCE, 10) || 50,
    NUM_STORES_PER_REQUEST_API_MAX: 200,
    STORE_LOCATOR_DISTANCE_UNIT: 'km',
    getDirectionURL: 'https://maps.google.com/?daddr='
}

// Per-locale defaults: country center for map, capital city for fallback store sorting, and country code.
// Add new locales here as additional regions are supported.
const LOCALE_DEFAULTS = {
    'en-GB': {
        countryCode: 'GB',
        defaultCenter: {latitude: 54.7023545, longitude: -3.2765753},
        defaultCityLocation: {latitude: 51.5074, longitude: -0.1278}, // London
        initialZoom: 5
    },
    'en-CA': {
        countryCode: 'CA',
        defaultCenter: {latitude: 56.1304, longitude: -106.3468},
        defaultCityLocation: {latitude: 45.4215, longitude: -75.6972}, // Ottawa
        initialZoom: 4
    },
    'fr-CA': {
        countryCode: 'CA',
        defaultCenter: {latitude: 56.1304, longitude: -106.3468},
        defaultCityLocation: {latitude: 45.4215, longitude: -75.6972}, // Ottawa
        initialZoom: 4
    },
    'en-US': {
        countryCode: 'US',
        defaultCenter: {latitude: 37.0902, longitude: -95.7129},
        defaultCityLocation: {latitude: 38.9072, longitude: -77.0369}, // Washington D.C.
        initialZoom: 4
    }
}

export const getLocaleDefaults = (locale) => {
    return LOCALE_DEFAULTS[locale] || LOCALE_DEFAULTS['en-GB']
}

// Country name aliases (lowercased) mapped to the matching entry in LOCALE_DEFAULTS.
// Only countries with store data are listed here; any other input falls back to the
// default geolocation-based search flow.
const SUPPORTED_COUNTRY_ALIASES = {
    GB: ['united kingdom', 'uk', 'u.k.', 'great britain', 'britain', 'england', 'royaume-uni', 'royaume uni'],
    CA: ['canada'],
    US: [
        'united states',
        'united states of america',
        'usa',
        'u.s.a.',
        'us',
        'u.s.',
        'america',
        'états-unis',
        'etats-unis',
        'états unis',
        'etats unis'
    ]
}

const normalizeCountryInput = (value) => {
    if (!value || typeof value !== 'string') return ''
    return value
        .trim()
        .toLowerCase()
        // Strip trailing punctuation like "Canada." or "Canada,"
        .replace(/[.,;]+$/g, '')
        // Collapse whitespace
        .replace(/\s+/g, ' ')
}

/**
 * Detects whether the provided search string matches one of the supported
 * country names. Returns the corresponding locale defaults entry (with the
 * matched countryCode) when found, otherwise null.
 */
export const detectCountryFromSearch = (searchString) => {
    const normalized = normalizeCountryInput(searchString)
    if (!normalized) return null

    const matchedCode = Object.keys(SUPPORTED_COUNTRY_ALIASES).find((code) =>
        SUPPORTED_COUNTRY_ALIASES[code].includes(normalized)
    )
    if (!matchedCode) return null

    // Find the first LOCALE_DEFAULTS entry that matches this country code so we
    // can reuse its default center/city for map positioning.
    const defaultsEntry = Object.values(LOCALE_DEFAULTS).find(
        (entry) => entry.countryCode === matchedCode
    )
    if (!defaultsEntry) return null

    return {
        ...defaultsEntry,
        countryCode: matchedCode
    }
}
